import { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './useLogin';

const useFeedback = (productId) => {
  const { user } = useContext(AuthContext);
  const [feedbacks, setFeedbacks] = useState([]);
  const [averageRating, setAverageRating] = useState({
    product_id: productId || 0,
    five_star_reviews: 0,
    four_star_reviews: 0,
    three_star_reviews: 0,
    two_star_reviews: 0,
    one_star_reviews: 0,
    total_reviews: 0,
    average_rating: 0.0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!productId || isNaN(productId)) {
      setError('Invalid product ID');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Fetch feedback
      console.log('Fetching feedback for product:', productId);
      const feedbackResponse = await axios.get(`https://inaaniserver-50c4305cc7f7.herokuapp.com/feedback/product/${productId}`);
      console.log('Raw Feedback API Response:', feedbackResponse.data);
      
      // Handle both single object and array responses
      let feedbackData = [];
      if (feedbackResponse.data) {
        if (Array.isArray(feedbackResponse.data)) {
          feedbackData = feedbackResponse.data;
        } else if (typeof feedbackResponse.data === 'object' && feedbackResponse.data.data) {
          feedbackData = Array.isArray(feedbackResponse.data.data)
            ? feedbackResponse.data.data
            : [feedbackResponse.data.data];
        } else if (typeof feedbackResponse.data === 'object') {
          feedbackData = [feedbackResponse.data];
        }
      }

      // Ensure each feedback has required fields
      feedbackData = feedbackData.map(feedback => ({
        fullname: feedback.fullname || feedback.user_name || 'Anonymous',
        comment: feedback.comment || '',
        rating: feedback.rating || 0,
        created_at: feedback.created_at || new Date().toISOString()
      }));

      console.log('Processed Feedback Data:', feedbackData);
      setFeedbacks(feedbackData);

      // Fetch average rating
      console.log('Fetching average rating for product:', productId);
      const averageResponse = await axios.get(`https://inaaniserver-50c4305cc7f7.herokuapp.com/feedback/average/${productId}`);
      console.log('Average Rating API Response:', averageResponse.data);
      console.log('Raw Average Rating Data:', JSON.stringify(averageResponse.data, null, 2));
      const ratingData = averageResponse.data && typeof averageResponse.data === 'object'
        ? averageResponse.data
        : {
            product_id: productId,
            five_star_reviews: 0,
            four_star_reviews: 0,
            three_star_reviews: 0,
            two_star_reviews: 0,
            one_star_reviews: 0,
            total_reviews: 0,
            average_rating: 0.0,
          };

      // If API returns zeros but we have feedbacks, calculate locally
      if (ratingData.total_reviews === 0 && feedbackData.length > 0) {
        const localRating = {
          product_id: productId,
          five_star_reviews: 0,
          four_star_reviews: 0,
          three_star_reviews: 0,
          two_star_reviews: 0,
          one_star_reviews: 0,
          total_reviews: feedbackData.length,
          average_rating: 0.0,
        };

        // Calculate star counts and average
        feedbackData.forEach(feedback => {
          const rating = feedback.rating;
          if (rating === 5) localRating.five_star_reviews++;
          else if (rating === 4) localRating.four_star_reviews++;
          else if (rating === 3) localRating.three_star_reviews++;
          else if (rating === 2) localRating.two_star_reviews++;
          else if (rating === 1) localRating.one_star_reviews++;
        });

        // Calculate average rating
        const totalStars = feedbackData.reduce((sum, feedback) => sum + feedback.rating, 0);
        localRating.average_rating = totalStars / feedbackData.length;

        console.log('Calculated Local Rating:', localRating);
        setAverageRating(localRating);
      } else {
        console.log('Using API Rating Data:', ratingData);
        setAverageRating(ratingData);
      }

      // Update product rating in products table
      try {
        const updateResponse = await axios.post('https://inaaniserver-50c4305cc7f7.herokuapp.com/products/edit', {
          id: productId
        });
        console.log('Product rating update response:', updateResponse.data);
      } catch (updateErr) {
        console.error('Error updating product rating:', updateErr.response?.data || updateErr.message);
        setError('Failed to update product rating in database: ' + (updateErr.response?.data?.message || updateErr.message));
      }
    } catch (err) {
      console.error('API Error:', err);
      console.error('Error Response:', err.response?.data);
      setError(err.response?.data?.error || 'Failed to fetch feedback data');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (productId) {
      fetchData();
    }
  }, [productId, fetchData]);

  const addFeedback = useCallback(async (rating, comment) => {
    if (!productId || !user?.id || !rating || rating < 0 || rating > 5) {
      throw new Error('Invalid feedback data');
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post('https://inaaniserver-50c4305cc7f7.herokuapp.com/feedback/add', {
        product_id: productId,
        user_id: user.id,
        rating,
        comment,
      });
      console.log('Add feedback response:', response.data);
      await fetchData();
      return response.data;
    } catch (err) {
      console.error('Add feedback error:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Failed to add feedback');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [productId, user?.id, fetchData]);

  const updateFeedback = useCallback(async (feedbackId, rating, comment) => {
    if (!feedbackId || !rating || rating < 0 || rating > 5) {
      throw new Error('Invalid feedback data');
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`https://inaaniserver-50c4305cc7f7.herokuapp.com/feedback/update/${feedbackId}`, {
        user_id: user?.id || null,
        rating,
        comment,
      });
      console.log('Update feedback response:', response.data);
      await fetchData();
      return response.data;
    } catch (err) {
      console.error('Update feedback error:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Failed to update feedback');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [user?.id, fetchData]);

  const deleteFeedback = useCallback(async (feedbackId) => {
    if (!feedbackId) {
      throw new Error('Invalid feedback ID');
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.delete(`https://inaaniserver-50c4305cc7f7.herokuapp.com/feedback/delete/${feedbackId}`);
      console.log('Delete feedback response:', response.data);
      await fetchData();
      return response.data;
    } catch (err) {
      console.error('Delete feedback error:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Failed to delete feedback');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchData]);

  return {
    feedbacks,
    averageRating,
    loading,
    error,
    addFeedback,
    updateFeedback,
    deleteFeedback,
  };
};

export default useFeedback;